import { z } from 'zod'

import type { GetMinimumProblemSchoolTypeGradeV2Data } from '~/api-spec/__generated__/main'
import { parseApiResponse } from '~/schema/apiBoundary'

import {
  AnswerUnitSchema,
  KeypadTypeSchema,
  type Problem,
  type ProblemMinimumTrieKey,
  ProblemMinimumTrieKeySchema,
  ProblemSchema,
} from './schemas'

// 서버는 문제 본체를 두 모양으로 보낸다. V1(ProblemResponse)은 도메인 스키마와 모양이 같고, V2
// (ProblemResponseV2)는 답 단위·키패드를 answerUnitSet·keypadTypeSet 으로 부르며 answerData 를 싣지 않는다.
// 도메인 어휘는 V1 쪽 이름이고 V2 는 여기서 푼다. 유형칩 대표 문제·유형칩 기반 추천이 V2 로 온다.
//
// V1 을 품는 응답의 생성 타입은 믿을 수 없다. 백엔드에 ProblemResponse 라는 클래스가 셋이고(문제 본체·
// 학습지 필터 추천·그룹 성취도 중첩) springdoc 이 이름으로만 스키마를 구분해 한 칸을 덮어쓴다. 그래서 V1 을
// 품는 adapter 는 각자 입력을 한 줄에서 unknown 으로 넘긴다. 2026-09-30 백엔드 develop(d90e66b2c) 의
// problem·worksheet·studentHistory 세 ProblemResponse.kt 로 확인.
//
// V2 를 품는 응답은 많아(유형칩·성취도·교재 문제) 이 스키마를 각자 끼워 검증한다. 이름 바꾸기는 스키마의 transform 이
// 아니라 fromProblemV2 로 따로 둔다. transform 에 두면 운영(lenient)에서 검증이 실패해 raw 가 넘어올 때 이름 바꾸기도
// 빠져 화면이 answerUnits·keypadTypes 를 잃는다. 품는 쪽 adapter 가 검증 결과에 fromProblemV2 를 적용한다.
export const ProblemV2WireSchema = ProblemSchema.omit({
  answerUnits: true,
  keypadTypes: true,
  answerData: true,
}).extend({
  answerUnitSet: z.array(AnswerUnitSchema),
  keypadTypeSet: z.array(KeypadTypeSchema),
})
export type ProblemV2Wire = z.output<typeof ProblemV2WireSchema>

export const fromProblemV2 = ({
  answerUnitSet,
  keypadTypeSet,
  ...rest
}: ProblemV2Wire): Problem => ({
  ...rest,
  answerUnits: answerUnitSet ?? [],
  keypadTypes: keypadTypeSet ?? [],
  answerData: null,
})

// 생성 타입은 trieKey 를 optional 로 그리지만 서버는 키를 빼지 않는다(Kotlin `String?`, Jackson 기본).
// 스키마가 실제 와이어를 검증하므로 입력을 이 한 줄에서만 unknown 으로 넘긴다.
const asWireMinimumTrieKey = (raw: GetMinimumProblemSchoolTypeGradeV2Data): unknown => raw

export const adaptProblemMinimumTrieKey = (
  raw: GetMinimumProblemSchoolTypeGradeV2Data,
): ProblemMinimumTrieKey =>
  parseApiResponse(ProblemMinimumTrieKeySchema, asWireMinimumTrieKey(raw), 'Problem')
